/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Effective-dated rate pricing - frontend mirror of backend/utils/ratePricing.js.
 *
 * A rate's price_schedule lists prices, each effective from a calendar day
 * onwards; the undated opening entry covers everything before the first change.
 * Cancelled entries are ignored. Keep in step with the backend copy.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Calendar day number. Strings ("YYYY-MM-DD" or ISO) use their literal date
 * part; Date objects (local noon throughout the invoice screens) use their
 * local calendar day - the same rules as calculateDays in invoiceCalculations.
 */
export const toDayNumber = (value) => {
  if (value == null || value === '') return null;
  let y; let m; let d;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    y = value.getFullYear(); m = value.getMonth(); d = value.getDate();
  } else {
    const parts = String(value).split('T')[0].split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    [y, m, d] = [parts[0], parts[1] - 1, parts[2]];
  }
  return Math.floor(Date.UTC(y, m, d, 12) / DAY_MS);
};

/** Local-noon Date for a day number (the invoice screens' date convention). */
export const fromDayNumber = (day) => {
  const dt = new Date(day * DAY_MS);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 12, 0, 0, 0);
};

export const activeSchedule = (rateObj) => {
  if (!rateObj) return [];
  const entries = (rateObj.price_schedule || [])
    .filter(e => e && !e.cancelled_at && e.rate != null)
    .map(e => ({
      rate: Number(e.rate),
      effective_from: e.effective_from || null,
      from: e.effective_from ? toDayNumber(e.effective_from) : -Infinity
    }))
    .sort((a, b) => a.from - b.from);
  if (entries.length === 0 || entries[0].from !== -Infinity) {
    entries.unshift({ rate: Number(rateObj.rate) || 0, effective_from: null, from: -Infinity });
  }
  return entries;
};

/** Price in effect on a date (default: today). */
export const priceOn = (rateObj, date) => {
  const day = toDayNumber(date || new Date());
  let price = 0;
  for (const entry of activeSchedule(rateObj)) {
    if (entry.from <= day) price = entry.rate;
    else break;
  }
  return price;
};

/**
 * Split an inclusive date range at every price change inside it.
 * @returns {Array<{ startDate, endDate, days, rate }>} local-noon dates
 */
export const splitByPrice = (rateObj, startDate, endDate) => {
  const startDay = toDayNumber(startDate);
  const endDay = toDayNumber(endDate);
  if (startDay == null || endDay == null || endDay < startDay) return [];
  const schedule = activeSchedule(rateObj);
  const segments = [];
  schedule.forEach((entry, i) => {
    const entryEnd = i + 1 < schedule.length ? schedule[i + 1].from - 1 : Infinity;
    const from = Math.max(startDay, entry.from);
    const to = Math.min(endDay, entryEnd);
    if (to >= from) {
      segments.push({ startDate: fromDayNumber(from), endDate: fromDayNumber(to), days: to - from + 1, rate: entry.rate });
    }
  });
  return segments;
};

/**
 * Billing basis of a rate. 'month' and 'year' rates are pro-rated by calendar
 * period (see billedUnits); any other basis ('day', or a label such as 'bag'
 * on a resource rate) bills compartments per day, as before.
 */
export const normalizeBasis = (basis) => {
  const b = String(basis || '').trim().toLowerCase();
  if (['month', 'months', 'monthly', 'mo'].includes(b)) return 'month';
  if (['year', 'years', 'yearly', 'annual', 'annually', 'yr'].includes(b)) return 'year';
  return 'day';
};

/**
 * How many of a rate's billing periods an inclusive date range covers.
 * day: the number of days. month / year: each calendar month (year) in the
 * range adds days used ÷ days in that month (year), so a full calendar month
 * or year is exactly 1 and short months and leap years use their real length.
 * Compartment cost = rate × quantity × billedUnits.
 */
export const billedUnits = (basis, startDate, endDate) => {
  const startDay = toDayNumber(startDate);
  const endDay = toDayNumber(endDate);
  if (startDay == null || endDay == null || endDay < startDay) return 0;
  const kind = normalizeBasis(basis);
  if (kind === 'day') return endDay - startDay + 1;

  let units = 0;
  let day = startDay;
  while (day <= endDay) {
    const d = new Date(day * DAY_MS);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const periodFirst = (kind === 'year' ? Date.UTC(y, 0, 1) : Date.UTC(y, m, 1)) / DAY_MS;
    const nextFirst = (kind === 'year' ? Date.UTC(y + 1, 0, 1) : Date.UTC(y, m + 1, 1)) / DAY_MS;
    const chunkEnd = Math.min(endDay, nextFirst - 1);
    units += (chunkEnd - day + 1) / (nextFirst - periodFirst);
    day = chunkEnd + 1;
  }
  return units;
};

/** Cost of a compartment line at one price across an inclusive date range. */
export const lineCost = (rate, quantity, basis, startDate, endDate) =>
  (Number(rate) || 0) * (Number(quantity) || 0) * billedUnits(basis, startDate, endDate);

/**
 * A rate for display: at least 2 decimals, more only when the price has them,
 * so $0.0035 / year does not show as $0.00.
 */
export const formatRate = (value) => {
  const [whole, frac = ''] = (Number(value) || 0).toFixed(6).replace(/0+$/, '').split('.');
  return `$${whole}.${frac.padEnd(2, '0')}`;
};

/** "0.4959 years", "1.5 months" - shown beside the days on month / year lines. */
export const formatBilledUnits = (basis, units) => {
  const kind = normalizeBasis(basis);
  if (kind === 'day') return '';
  const n = Number((Number(units) || 0).toFixed(4));
  return `${n} ${kind}${n === 1 ? '' : 's'}`;
};

/**
 * Price a compartment usage row across its dates.
 * Rows without a schedule (e.g. stored billed periods) keep their flat rate.
 * Month / year rates are pro-rated by calendar period (billedUnits).
 * @returns {{ segments, totalCost, rate, units, hasPriceChange }}
 */
export const priceChamberRow = (row, startDate, endDate) => {
  const quantity = Number(row.quantity) || 0;
  const basis = row.rateBasis ?? row.rate_basis;
  const schedule = row.priceSchedule ? { rate: row.rate, price_schedule: row.priceSchedule } : { rate: row.rate };
  const segments = splitByPrice(schedule, startDate, endDate).map(s => {
    const units = billedUnits(basis, s.startDate, s.endDate);
    return { ...s, units, cost: s.rate * quantity * units };
  });
  const totalCost = segments.reduce((sum, s) => sum + s.cost, 0);
  return {
    segments,
    totalCost,
    rate: segments.length ? segments[0].rate : (Number(row.rate) || 0),
    units: segments.reduce((sum, s) => sum + s.units, 0),
    hasPriceChange: segments.length > 1
  };
};
